import { useFormTableCellDirtyContext } from "@/contexts/FormTableCellDirtyContext";
import { isValueDifferent } from "@/lib/utils/isValueDifferent";
import { Pencil } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";

/**
 * Shows how many table rows still differ from the last saved snapshot (`baseline` from
 * FormTableCellDirtyProvider / editionHighlight).
 *
 * Counts by comparing live values to baseline — same as DirtyAwareCell — not React Hook Form’s
 * `dirtyFields`, so reverting a value to match the server clears the row from the count even if RHF
 * still marks the field dirty. We scan all row ids (not only dirtyFields) because
 * `setValue(..., { shouldDirty: false })` can change values without dirty flags.
 *
 * Renders nothing when there is no FormTableCellDirtyProvider (e.g. table without editionHighlight).
 */
export const ListDirtyRowsStatus = () => {
  const { t } = useTranslation();
  const ctx = useFormTableCellDirtyContext();

  const recordKey = ctx?.recordKey ?? "entries";

  const currentRecords = useWatch({
    name: recordKey,
    disabled: ctx == null,
  }) as Record<string, Record<string, unknown>> | undefined;

  const count = useMemo(() => {
    if (!ctx) return 0;

    const baselineRows =
      (ctx.baseline as Record<string, Record<string, Record<string, unknown>>>)[
        recordKey
      ] ?? {};
    return countRowsDifferingBetweenSnapshots(
      baselineRows,
      currentRecords ?? {},
    );
  }, [ctx, recordKey, currentRecords]);

  if (count === 0) {
    return null;
  }

  return (
    <span className="flex items-center gap-2 shrink-0">
      <Pencil className="w-4 h-4" />
      <span>{t("table.dirtyRowsCount", { count })}</span>
    </span>
  );
};

/**
 * Same equality rules as DirtyAwareCell (via isValueDifferent): numeric coercion, trimmed strings, etc.
 * Used so the footer stays consistent with per-cell amber rings.
 */
const rowDiffersFromBaseline = (
  baselineRow: Record<string, unknown>,
  currentRow: Record<string, unknown>,
): boolean => {
  for (const k of new Set([
    ...Object.keys(baselineRow),
    ...Object.keys(currentRow),
  ])) {
    if (isValueDifferent(baselineRow[k], currentRow[k])) return true;
  }
  return false;
};

/** How many row ids have at least one field differing between baseline snapshot and current form slice. */
const countRowsDifferingBetweenSnapshots = (
  baselineRows: Record<string, Record<string, unknown>>,
  currentRows: Record<string, Record<string, unknown>>,
): number => {
  const ids = new Set([
    ...Object.keys(baselineRows),
    ...Object.keys(currentRows),
  ]);
  let n = 0;
  for (const id of ids) {
    if (rowDiffersFromBaseline(baselineRows[id] ?? {}, currentRows[id] ?? {})) {
      n++;
    }
  }
  return n;
};
